// src/pages/processifyd/ProcessifydRACIPersons.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faList,
} from "@fortawesome/free-solid-svg-icons";

const ProcessifydRACIPersons = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [persons, setPersons] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [loading, setLoading] = useState(false);

  const initialForm = {
    personThink5WhysCode: "",
    personName: "",
    personDetails: "",
    clientName: "",
    active: "Yes",
  };

  const [formData, setFormData] = useState(initialForm);

  // Sample Demo Data
  const demoPersons = [
    {
      id: 1,
      personThink5WhysCode: "T5W-001",
      personName: "Sanjeev Kumar",
      personDetails: "Survey Program Owner and overall accountable person.",
      clientName: "21st Century Financial Services",      
      active: "Yes",
      createdDate: "18/03/26 13:40",
      lastUpdateDate: "18/03/26 13:40",
    },
    {
      id: 2,
      personThink5WhysCode: "T5W-002",
      personName: "Priya Sharma",
      personDetails: "Handles NPS tracking and customer sentiment analysis.",
      clientName: "21st Century Financial Services",      
      active: "Yes",
      createdDate: "18/03/26 13:41",
      lastUpdateDate: "18/03/26 13:41",
    },
    {
      id: 3,
      personThink5WhysCode: "T5W-003",
      personName: "Rahul Mehta",
      personDetails: "Performs data analysis and generates reports.",
      clientName: "21st Century Financial Services",
      active: "Yes",
      createdDate: "18/03/26 13:42",
      lastUpdateDate: "18/03/26 13:42",
    },
    {
      id: 4,
      personThink5WhysCode: "T5W-004",
      personName: "Anita Desai",
      personDetails: "Responsible for compliance checks and audit readiness.",
      clientName: "21st Century Financial Services",      
      active: "Yes",
      createdDate: "18/03/26 13:43",
      lastUpdateDate: "18/03/26 13:43",
    },
    {
      id: 5,
      personThink5WhysCode: "T5W-005",
      personName: "Vikram Singh",
      personDetails: "Leads CAPA actions based on feedback insights.",
      clientName: "21st Century Financial Services",      
      active: "Yes",
      createdDate: "18/03/26 13:44",
      lastUpdateDate: "18/03/26 13:44",
    },
  ];

  const loadSample = () => {
    setPersons(demoPersons);
  };

  const handleAddNew = () => {
    setEditingPerson(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (person) => {
    setEditingPerson(person);
    setFormData({
      personThink5WhysCode: person.personThink5WhysCode || "",
      personName: person.personName || "",
      personDetails: person.personDetails || "",
      clientName: person.clientName || "",
      active: person.active || "Yes",
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this person? This action cannot be undone.")) return;
    setPersons((prev) => prev.filter((p) => p.id !== id));
  };

  const handleOpenPerson = (person) => {
    navigate(`/processifyd-raciperson/${person.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.personThink5WhysCode.trim() || !formData.personName.trim()) {
      alert("Code and Person Name are required.");
      return;
    }

    const newPerson = {
      id: editingPerson?.id || Date.now(),
      ...formData,
      createdDate: editingPerson?.createdDate || new Date().toLocaleString("en-IN"),
      lastUpdateDate: new Date().toLocaleString("en-IN"),
    };

    if (editingPerson) {
      setPersons((prev) =>
        prev.map((p) => (p.id === editingPerson.id ? newPerson : p))
      );
    } else {
      setPersons((prev) => [...prev, newPerson]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // Filter + Pagination
  const filtered = persons.filter(
    (p) =>
      p.personName.toLowerCase().includes(search.toLowerCase()) ||
      p.clientName.toLowerCase().includes(search.toLowerCase()) ||
      p.personThink5WhysCode.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const [showList, setShowList] = useState(false);
    
  const handleShowMyPersons = async () => {
    try {
      setLoading(true);
      setPersons([]);
      setShowList(true);
    } catch (error) {
      console.error("Error fetching RACI Persons:", error);
    } finally {
      setLoading(false);
    }
  };

  
  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            👥 Processifyd
          </div>
          <div className="text-xs text-gray-400">
            RACI Groups Master Management
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 hover:bg-teal-900/30 rounded-md"
            >
              🔐
            </button>
          )}
        </div>

        <button
          className="md:hidden text-2xl text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          {isLoggedIn && (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="w-12 px-2 py-1 bg-red-700 rounded-md">⏻</button>
            </div>
          )}
        </div>
      )}

      <div className="min-h-[75vh]">
        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex flex-row gap-3">
            <button
              onClick={loadSample}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-56 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              Load Sample RACI Persons
            </button>
            <button
              onClick={handleShowMyPersons}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faList} />
              My RACI Persons List
            </button>
            <button
              onClick={handleAddNew}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              New RACI Perosn
            </button>
          </div>

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by code or person name or client name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />
          </div>
        </div>

        {/* Persons Cards */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map((person) => (
            <div
              key={person.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-white group-hover:text-cyan-300 transition-colors">
                    {person.personThink5WhysCode} : {person.personName}
                  </h3>
                  <div className="text-sm text-gray-400 mt-1">
                    Client Name: <span className="text-cyan-300">{person.clientName}</span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${person.active === "Yes" ? "bg-emerald-600" : "bg-red-600"}`}>
                    {person.active === "Yes" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpenPerson(person)}
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  Open
                </button>

                <button
                  onClick={() => handleEdit(person)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Edit Group"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>

                <button
                  onClick={() => handleDelete(person.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Delete Group"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search ? "No person match your search" : "No person yet. Click 'New Person' or 'Load Sample Persons'"}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {showList && (
          <div className="mt-4 bg-black text-white rounded-lg p-4">
            {loading ? (
              <p className="text-yellow-200">Loading RACI Persons...</p>
            ) : persons.length === 0 ? (
              <p className="text-yellow-200">No RACI Persons found.</p>
            ) : (
              <ul className="space-y-2">
                {persons.map((per) => (
                  <li
                    key={per.id}
                    className="border border-gray-700 rounded p-2"
                  >
                    <p className="font-semibold text-gray-100">{per.personName}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Add / Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700 max-h-[92vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingPerson ? "Edit Person" : "New Person"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white w-12 text-2xl"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm mb-1.5">Person Think5Whys Code <span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.personThink5WhysCode}
                    onChange={(e) => setFormData({ ...formData, personThink5WhysCode: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="Input Persons Think5Whys 16 Characters Code"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Person Name <span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.personName}
                    onChange={(e) => setFormData({ ...formData, personName: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. BKC 1, Andheri East"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Person Details <span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.personDetails}
                    onChange={(e) => setFormData({ ...formData, personDetails: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. This is client resource"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Client Name<span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. 21st Century Financial Services"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Active <span className="text-red-400">*</span></label>
                    <select
                      value={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                      className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
                  >
                    {editingPerson ? "Update Person" : "Create Person"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-8">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProcessifydRACIPersons;